import { createPortal } from 'react-dom';
import { useState } from 'react';
import { ThumbsDown, ThumbsUp, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { doctorSubmitFeedback } from '../services/doctorApi';
import { useDoctorSession } from '../contexts/DoctorSessionContext';
import { markFeedbackMilestoneShown } from '../utils/doctorFeedbackMilestones';

export default function DoctorFeedbackModal() {
  const { t } = useLanguage();
  const { doctor, feedbackPrompt, clearFeedbackPrompt, showToast } = useDoctorSession();
  const [rating, setRating] = useState(null);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!feedbackPrompt) return null;

  const close = () => {
    markFeedbackMilestoneShown(doctor?.id, feedbackPrompt.milestone);
    setRating(null);
    setText('');
    setError('');
    clearFeedbackPrompt();
  };

  const handleSubmit = async () => {
    if (!rating) return;
    setSubmitting(true);
    setError('');
    try {
      await doctorSubmitFeedback({
        rating,
        text: text.trim(),
        milestone: feedbackPrompt.milestone,
      });
      markFeedbackMilestoneShown(doctor?.id, feedbackPrompt.milestone);
      clearFeedbackPrompt();
      setRating(null);
      setText('');
      showToast(t('thanksFeedback'));
    } catch (err) {
      setError(err.message || t('feedbackSubmitError'));
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[400] flex items-end justify-center bg-black/40 p-4 sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="doctor-feedback-title"
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p id="doctor-feedback-title" className="text-lg font-semibold text-gray-900">
              {t('doctorHowDoYouFindIt')}
            </p>
            <p className="mt-1 text-sm text-gray-500">{t('doctorFeedbackOptionalHint')}</p>
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label={t('skip')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setRating('up')}
            className={`flex h-12 w-12 items-center justify-center rounded-xl border-2 ${
              rating === 'up' ? 'border-primary bg-primary-light' : 'border-gray-200'
            }`}
            aria-label={t('doctorFeedbackThumbsUp')}
          >
            <ThumbsUp className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => setRating('down')}
            className={`flex h-12 w-12 items-center justify-center rounded-xl border-2 ${
              rating === 'down' ? 'border-primary bg-primary-light' : 'border-gray-200'
            }`}
            aria-label={t('doctorFeedbackThumbsDown')}
          >
            <ThumbsDown className="h-5 w-5" />
          </button>
        </div>

        {rating && (
          <>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t('feedbackPlaceholder')}
              rows={3}
              className="mt-3 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            />
            {error && <p className="mt-2 text-sm text-critical">{error}</p>}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="btn-primary mt-3 w-full text-sm disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? t('feedbackSubmitting') : t('submit')}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
